import { ICPaasConfig, UsageQuery, UsageTotalsQuery } from './types';
import type { ApiError, OkResponse, Session, UsageRecord, UsageTotals } from './protocol';
import { AuthManager } from './auth';

/** Dates go on the wire as ISO timestamps, which is what the server parses. */
function isoDate(value: Date | string): string {
  return value instanceof Date ? value.toISOString() : value;
}

/** `?from=…&to=…&apiKeyId=…`, leaving out what was not given. */
function usageQueryString(query: UsageQuery | UsageTotalsQuery): string {
  const params = new URLSearchParams();
  if (query.from !== undefined) params.set('from', isoDate(query.from));
  if (query.to !== undefined) params.set('to', isoDate(query.to));
  if (query.apiKeyId !== undefined) params.set('apiKeyId', String(query.apiKeyId));
  if ('limit' in query && query.limit !== undefined) params.set('limit', String(query.limit));
  if ('offset' in query && query.offset !== undefined) params.set('offset', String(query.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * REST client for sessions and usage data.
 */
export class APIClient {
  private config: ICPaasConfig;
  private auth: AuthManager;

  constructor(config: ICPaasConfig, auth: AuthManager) {
    this.config = config;
    this.auth = auth;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.auth.getBaseURL()}${path}`, {
      ...init,
      credentials: this.auth.credentialsMode(),
      headers: this.auth.headers(init.headers as Record<string, string>),
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as ApiError | null;
      throw new Error(body?.error || `Request to ${path} failed: ${response.status}`);
    }
    return response.json() as Promise<T>;
  }

  getSessions(): Promise<Session[]> {
    return this.request<Session[]>('/api/sessions');
  }

  /** One session, including its full transcript. */
  getSession(id: string): Promise<Session> {
    return this.request<Session>(`/api/sessions/${encodeURIComponent(id)}`);
  }

  deleteSession(id: string): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Metered events, newest first; one page of `limit` (default 100) from `offset`. */
  getUsage(query: UsageQuery = {}): Promise<UsageRecord[]> {
    return this.request<UsageRecord[]>(`/api/usage${usageQueryString(query)}`);
  }

  /** Like getUsage, plus how many records match in total (for a pager). */
  async getUsagePage(query: UsageQuery = {}): Promise<{ rows: UsageRecord[]; total: number }> {
    const path = `/api/usage${usageQueryString(query)}`;
    const response = await fetch(`${this.auth.getBaseURL()}${path}`, {
      credentials: this.auth.credentialsMode(),
      headers: this.auth.headers(),
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as ApiError | null;
      throw new Error(body?.error || `Request to ${path} failed: ${response.status}`);
    }
    const rows = (await response.json()) as UsageRecord[];
    const total = Number(response.headers.get('X-Total-Count'));
    return { rows, total: Number.isFinite(total) ? total : rows.length };
  }

  /** Totals over every usage row in the range. */
  getUsageTotals(query: UsageTotalsQuery = {}): Promise<UsageTotals> {
    return this.request<UsageTotals>(`/api/usage/summary${usageQueryString(query)}`);
  }
}
